import React, { FC, useCallback, useMemo, useState } from "react";
import { Button, notification, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../app";
import {
  changeHasBeenEdited,
  setCancelEditModalOpen,
  setEditAfterExportMode,
  setEditingPausedTagVisible,
} from "../../../../app/store/appSlice";
import {
  useLazyGetDocumentSetDataQuery,
  useUpdateEditModeMutation,
} from "../../../../documentSet/store/documentSetApi";
import { setActiveDocumentSet } from "../../../../documentSet/store/documentSetSlice";
import { addAnnotationsForDocument } from "../../../../annotation/store/annotationSlice";
import { buildAnnotationView } from "../../../../annotation/utils/buildAnnotationView";
import { setCategorizationAnnotationsBatch } from "../../../../metadata/store/metadataSlice";
import { updateCategorizationAnnotationsWithNewGroupBlockId } from "../../../../documentTypes/utils/synchronizeAnnotationHelper";
import useCategorizationDataFetcher from "../../../../metadata/hooks/useCategorizationDataFetcher";
import useMultipleGroupBlocks from "../../../../configMap/hooks/useMultipleGroupBlocks";
import { postData } from "../../../../configMap/utils/entityUtils";
import documentIdsSelector from "../../../../document/selectors/documentIdsSelector";
import { calculateHideDelay } from "../../../../common/utilities/text";
import ConfirmationModal from "../../../../common/modals/confirmationModal/ConfirmationModal";
import { getErrorStatus } from "../../../utils/errorHelpers";

type Props = {
  documentSetId: string;
};

const EditAfterExportButtons: FC<Props> = ({ documentSetId }) => {
  const { t } = useTranslation(["annotationSideControls", "detail"]);
  const dispatch = useAppDispatch();

  const [updateEditMode, { isLoading: isUpdatingMode }] =
    useUpdateEditModeMutation();
  const [getDocumentSetData] = useLazyGetDocumentSetDataQuery();
  const { fetchCategorizationAnnotationData } = useCategorizationDataFetcher();
  const { setInitialMultipleGroupBlocksCount } = useMultipleGroupBlocks();
  const [isConfirming, setIsConfirming] = useState(false);

  // Not selectAnnotationReducer: that one returns the snapshot while the toggle is on
  // "exported", and confirming has to save what was edited, never a past version.
  const tableControlAnnotations = useAppSelector(
    (state) => state.annotationReducer.tableControlAnnotations,
  );
  const sideControlAnnotations = useAppSelector(
    (state) => state.annotationReducer.sideControlAnnotations,
  );
  const annotations = useMemo(
    () => [...tableControlAnnotations, ...sideControlAnnotations],
    [tableControlAnnotations, sideControlAnnotations],
  );
  const { categorizationAnnotations } = useAppSelector(
    (state) => state.metadataReducer.categorizationState,
  );
  const documentIds = useAppSelector(documentIdsSelector);
  const hasBeenEdited = useAppSelector(
    (state) => state.appReducer.hasBeenEdited,
  );
  const activeDocumentSet = useAppSelector(
    (state) => state.documentSetsReducer.activeDocumentSet,
  );
  const configMap = useAppSelector((state) => state.configMapReducer.configMap);
  const cancelModalOpen = useAppSelector(
    (state) => state.appReducer.cancelEditModalOpen,
  );

  // Cancelling or confirming ends the session, so a paused tag from an earlier pause in
  // that same session no longer applies.
  const exitEditMode = useCallback(() => {
    dispatch(setEditAfterExportMode(false));
    dispatch(changeHasBeenEdited(false));
    dispatch(setEditingPausedTagVisible(false));
    dispatch(setCancelEditModalOpen(false));
  }, [dispatch]);

  const handlePause = useCallback(async () => {
    // Release the backend edit-mode lock so it can be re-acquired on resume.
    // Local unsaved changes are preserved via the hasBeenEdited flag; the
    // standard ConfirmLeaveModal still handles save-on-navigate.
    try {
      await updateEditMode({ documentSetId, action: "CANCEL" }).unwrap();
    } catch (error) {
      // 409 means the backend is already out of edit mode — safe to ignore.
      if (getErrorStatus(error) !== 409) {
        console.error("Failed to pause edit mode on backend:", error);
      }
    }
    dispatch(setEditAfterExportMode(false));
    if (hasBeenEdited) {
      dispatch(setEditingPausedTagVisible(true));
    }
  }, [updateEditMode, documentSetId, dispatch, hasBeenEdited]);

  // Edits are only sent to the backend on confirm, so discarding them means putting the
  // stored annotations back in the reducer. Built with the same pipeline as the initial
  // load so rows and indices come back exactly as they were.
  const restoreStoredAnnotations = useCallback(async () => {
    const documentSet = await getDocumentSetData(documentSetId).unwrap();
    const documents = documentSet.documents ?? [];
    if (documents.length === 0) {
      return;
    }

    const storedCategorizations =
      updateCategorizationAnnotationsWithNewGroupBlockId(
        documents
          .flatMap((document) => document.categorizationAnnotations ?? [])
          .map((annotation) => ({
            ...annotation,
            groupBlockId: annotation.groupBlockId ?? undefined,
            originalIndex: annotation.index,
          })),
        configMap?.groupBlocks,
      );

    const view = buildAnnotationView(
      documents.flatMap((document) => document.entityAnnotations ?? []),
      storedCategorizations,
      configMap,
    );

    const restoredCategorizations = await fetchCategorizationAnnotationData(
      view.categorizationAnnotations,
    );

    dispatch(setCategorizationAnnotationsBatch(restoredCategorizations));
    dispatch(
      addAnnotationsForDocument({
        documentId: documents[0].id,
        annotations: view.annotations,
      }),
    );

    // Rows added while editing are gone, so the row count has to come down with them.
    if (configMap) {
      setInitialMultipleGroupBlocksCount(
        configMap,
        view.annotations,
        restoredCategorizations,
      );
    }
  }, [
    getDocumentSetData,
    documentSetId,
    configMap,
    fetchCategorizationAnnotationData,
    setInitialMultipleGroupBlocksCount,
    dispatch,
  ]);

  const handleCancelConfirmed = useCallback(async () => {
    dispatch(setCancelEditModalOpen(false));
    try {
      await updateEditMode({ documentSetId, action: "CANCEL" }).unwrap();
      await restoreStoredAnnotations();
      exitEditMode();
    } catch (error) {
      console.error("Failed to cancel edit mode:", error);
      notification.error({
        title: t("notifications:editLabels.cancelFailed.message", {
          defaultValue: "Could not cancel edit mode",
        }),
      });
    }
  }, [
    updateEditMode,
    documentSetId,
    restoreStoredAnnotations,
    exitEditMode,
    dispatch,
    t,
  ]);

  const handleConfirm = useCallback(async () => {
    setIsConfirming(true);
    try {
      if (hasBeenEdited) {
        await postData(
          documentIds,
          annotations,
          categorizationAnnotations || [],
        );
      }
      await updateEditMode({ documentSetId, action: "CONFIRM" }).unwrap();
      // Confirming pins a new annotation version on the backend. Mirror it here so the
      // exported/current toggle shows up straight away instead of only after a reload.
      if (activeDocumentSet?.id === documentSetId) {
        dispatch(
          setActiveDocumentSet({
            ...activeDocumentSet,
            annotationVersion: (activeDocumentSet.annotationVersion ?? 0) + 1,
          }),
        );
      }
      exitEditMode();
      const successMessage = t(
        "annotationSideControls:editAfterExport.confirmed",
        { defaultValue: "Changes confirmed, labels updated" },
      );
      notification.success({
        title: successMessage,
        duration: calculateHideDelay(successMessage),
      });
    } catch (error) {
      console.error("Failed to confirm edit-mode changes:", error);
      notification.error({
        title: t("notifications:editLabels.confirmFailed.message", {
          defaultValue: "Could not confirm changes",
        }),
      });
    } finally {
      setIsConfirming(false);
    }
  }, [
    hasBeenEdited,
    documentIds,
    annotations,
    categorizationAnnotations,
    updateEditMode,
    documentSetId,
    activeDocumentSet,
    dispatch,
    exitEditMode,
    t,
  ]);

  const disableAll = isUpdatingMode || isConfirming;

  return (
    <>
      <div className="action-button__container">
        <Button
          id="action-edit-cancel"
          onClick={() => dispatch(setCancelEditModalOpen(true))}
          disabled={disableAll}
          icon={<i className="bi bi-x" />}
          ghost
        >
          {t("annotationSideControls:editAfterExport.cancel", {
            defaultValue: "Cancel",
          })}
        </Button>
        <Button
          id="action-edit-pause"
          onClick={handlePause}
          disabled={disableAll}
          icon={<i className="bi bi-pause" />}
          ghost
        >
          {t("annotationSideControls:editAfterExport.pause", {
            defaultValue: "Pause",
          })}
        </Button>
        <Tooltip
          title={t("annotationSideControls:editAfterExport.confirmTooltip", {
            defaultValue:
              "Confirm that all annotations have been reviewed and are correct.",
          })}
        >
          <Button
            id="action-edit-confirm"
            type="primary"
            onClick={handleConfirm}
            loading={isConfirming}
            disabled={disableAll}
            icon={<i className="bi bi-check" />}
          >
            {t("annotationSideControls:editAfterExport.confirm", {
              defaultValue: "Confirm",
            })}
          </Button>
        </Tooltip>
      </div>
      <ConfirmationModal
        open={cancelModalOpen}
        onCancel={() => dispatch(setCancelEditModalOpen(false))}
        onClose={() => dispatch(setCancelEditModalOpen(false))}
        onConfirm={handleCancelConfirmed}
        title={t("annotationSideControls:editAfterExport.cancelModalTitle", {
          defaultValue: "Discard your edits?",
        })}
        description={t(
          "annotationSideControls:editAfterExport.cancelModalDescription",
          {
            defaultValue:
              "Unsaved changes will be discarded and you'll exit edit mode.",
          },
        )}
        confirmText={t(
          "annotationSideControls:editAfterExport.discardChanges",
          {
            defaultValue: "Discard Changes",
          },
        )}
        cancelText={t("annotationSideControls:editAfterExport.keepEditing", {
          defaultValue: "Keep Editing",
        })}
        type="warning"
      />
    </>
  );
};

export default EditAfterExportButtons;
